// Story 11.5 — Pure-logic del CommandPalette: tipos, prefijos, fuzzy filter,
// LRU recientes en localStorage. Aislado de React para tests sin deps.

export type CommandCategory = 'files' | 'navigation' | 'flows' | 'actions' | 'templates';

export interface CommandItem {
  /** Único, estable. Usado como key + recents. */
  id: string;
  category: CommandCategory;
  label: string;
  /** Texto secundario opcional (ej. atajo). */
  hint?: string;
  /** Texto adicional para fuzzy matching. */
  keywords?: string[];
}

export type ScoredCommand = CommandItem & { score: number };

const PREFIX_TO_CATEGORY: Record<string, CommandCategory | null> = {
  '>': 'actions',
  ':': 'files',
  '@': 'flows',
  '#': 'navigation',
};

export interface ParsedQuery {
  /** Si el input empezó con un prefijo, esta es la categoría filtrada. */
  category: CommandCategory | null;
  /** El prefijo encontrado (e.g. ">"), o null. */
  prefix: string | null;
  /** El query sin el prefijo. */
  search: string;
}

export function parseQuery(input: string): ParsedQuery {
  if (input.length === 0) return { category: null, prefix: null, search: '' };
  const head = input[0];
  if (head && head in PREFIX_TO_CATEGORY) {
    const category = PREFIX_TO_CATEGORY[head] ?? null;
    return {
      category,
      prefix: head,
      search: input.slice(1).trim(),
    };
  }
  return { category: null, prefix: null, search: input };
}

/**
 * Score fuzzy: char-by-char en el label/keywords. Substring exact gana,
 * matches prefix mejor que matches en medio. Returns -1 cuando no matchea.
 */
export function scoreCommand(item: CommandItem, query: string): number {
  if (query.length === 0) return 0;
  const q = query.toLowerCase();
  const haystacks = [item.label, ...(item.keywords ?? [])].map((s) => s.toLowerCase());
  let best = -1;
  for (const h of haystacks) {
    if (h === q) return 1000;
    if (h.startsWith(q)) return Math.max(best, 500 - (h.length - q.length));
    const idx = h.indexOf(q);
    if (idx >= 0) {
      best = Math.max(best, 200 - idx - (h.length - q.length));
      continue;
    }
    // Subsequence match (fuzzy): cada char del query aparece en orden.
    let i = 0;
    let lastIdx = -1;
    let chars = 0;
    for (let h_i = 0; h_i < h.length && i < q.length; h_i++) {
      if (h[h_i] === q[i]) {
        if (lastIdx >= 0 && h_i - lastIdx > 1) chars--;
        lastIdx = h_i;
        chars++;
        i++;
      }
    }
    if (i === q.length) {
      best = Math.max(best, 50 + chars);
    }
  }
  return best;
}

/**
 * Filtra y ordena items. Los `recentIds` se boostean al top del score.
 */
export function filterCommands(
  items: CommandItem[],
  rawInput: string,
  recentIds: string[] = [],
): ScoredCommand[] {
  const { category, search } = parseQuery(rawInput);
  const recentSet = new Set(recentIds);
  const scored: ScoredCommand[] = [];
  for (const item of items) {
    if (category != null && item.category !== category) continue;
    const score = scoreCommand(item, search);
    if (search.length > 0 && score < 0) continue;
    const recentBoost = recentSet.has(item.id) ? 50 : 0;
    scored.push({ ...item, score: score + recentBoost });
  }
  scored.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  return scored;
}

/** Agrupa scored items por categoría preservando el orden interno. */
export function groupByCategory(items: ScoredCommand[]): Map<CommandCategory, ScoredCommand[]> {
  const map = new Map<CommandCategory, ScoredCommand[]>();
  for (const item of items) {
    const list = map.get(item.category) ?? [];
    list.push(item);
    map.set(item.category, list);
  }
  return map;
}

// --- Recientes en localStorage ---

const MAX_RECENT = 5;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function recentKey(botId: string | number | null): string {
  return `revel-cmd-recent-${botId ?? 'global'}`;
}

export function loadRecent(storage: StorageLike, botId: string | number | null): string[] {
  try {
    const raw = storage.getItem(recentKey(botId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

export function saveRecent(
  storage: StorageLike,
  botId: string | number | null,
  itemId: string,
): string[] {
  const current = loadRecent(storage, botId);
  const next = [itemId, ...current.filter((id) => id !== itemId)].slice(0, MAX_RECENT);
  try {
    storage.setItem(recentKey(botId), JSON.stringify(next));
  } catch {
    /* ignore quota exceeded */
  }
  return next;
}
